import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import styles from 'components/medicalHistory/page.module.scss';
import Stack from '@mui/material/Stack';
import Button from 'components/common/button';

import {
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import { CommonIssueItem } from 'types/medicalHistory';
import { actionTypeOfMedicalHistory } from 'reducer/actionType';
import MetaTag from 'components/meta';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { NullablePatientDTO } from 'types/patients';
import clsx from 'clsx';
import concat from 'lodash/concat';
import keyBy from 'lodash/keyBy';
import partition from 'lodash/partition';

export const reviewOfSystemMappingData: CommonIssueItem[] = [
  {
    name: 'Constitutional (general body)',
    anchor: 'Constitutional',
    title: 'Constitutional',
    options: ['Weight Gain', 'Weight Loss', 'Fatigue', 'Fever/Chills', 'Loss of Appetite'],
  },
  {
    name: 'Joints, Muscles, and Bones',
    anchor: 'Musculoskeletal',
    title: 'Musculoskeletal',
    options: ['Back Pain', 'Joint Pain', 'Muscle Aches'],
  },
  {
    name: 'Neurological (nerves & brain)',
    anchor: 'Neurological',
    title: 'Neurological',
    options: [
      'Dizziness',
      'Fainting',
      'Headaches',
      'Numbness or Tingling',
      'Parelysis of Extremities',
      'Scalp Tenderness',
      'Seizures or Convulsions',
      'Stroke',
      'Tremor',
      'Weakness',
    ],
  },
  {
    name: 'Cardiovascular (heart & blood vessels)',
    anchor: 'Cardiovascular',
    title: 'Cardiovascular',
    options: [
      'Chest Pain',
      'Irregular Heart Beat',
      'Racing Pulse',
      'Shortness of Breath',
      'Swelling of the Feet',
    ],
  },
  {
    name: 'Dermatology (skin)',
    anchor: 'Dermatological',
    title: 'Dermatological',
    options: [
      'Mole',
      'Loss of Hair',
      'Rash',
      'Severe Itching',
      'Skin Cancer',
      'Skin Sores',
    ],
  },
  {
    name: 'Hematology (blood)',
    anchor: 'HematologyOncology',
    title: 'Hematology / Oncology',
    options: [
      'Easy Bruising',
      'Prolonged Bleeding',
      'Taking Blood Thinners',
      'Blood Clots',
    ],
  },
  {
    name: 'Genitourinary (kidney/bladder & genitals)',
    anchor: 'Genitourinary',
    title: 'Genitourinary',
    options: [
      'Genital Sores or Ulcers',
      'Kidney Failure / Problems',
      'Kidney Stones',
      'Painful / Difficult Urination (Prostatitis)',
      'Testicular Pain',
      'Urinary Discharge',
    ],
  },
  {
    name: 'ENT (ears, nose, & throat)',
    anchor: 'ENT',
    title: 'ENT',
    options: [
      'Hearing Loss',
      'Sore Throat',
      'Runny Nose',
      'Dry Mouth',
      'Jaw Claudication (pain in jaw when chewing)',
      'Ear Ache',
      'Trouble Swallowing',
    ],
  },
  {
    name: 'Respiratory (lungs & breathing)',
    anchor: 'Respiratory',
    title: 'Respiratory',
    options: [
      'Wheezing',
      'Cough',
      'Coughing up Blood',
      'Severe or Frequent Colds',
      'Difficulty Breathing',
    ],
  },
  {
    name: 'Gastrointestinal (stomach & intestines)',
    anchor: 'Gastrointestinal',
    title: 'Gastrointestinal',
    options: [
      'Abdominal Pain',
      'Nausea',
      'Diarrhea',
      'Bloody Stools',
      'Stomach Ulcers',
      'Constipation',
      'Trouble Swallowing',
      'Jaundice / Yellow Skin',
    ],
  },
];

export type ReviewOfSystemOption = {
  name: string;
  discriminator?: string;
  readOnly?: boolean;
};

function mapOptionWithSelectedData(
  predefineList: ReviewOfSystemOption[],
  selectedList: ReviewOfSystemOption[],
) {
  const predefineListMap = keyBy(predefineList, 'name');

  const [
    listedItems, unlistedItems,
  ] = partition(selectedList, (item) => predefineListMap[item.name]);

  const selectedOption = concat(listedItems, unlistedItems.map(
    (item) => ({ ...item, readOnly: true }),
  ));
  const updatedOptions = concat(predefineList, unlistedItems);

  return { selectedOption, updatedOptions };
}

const getSubmitState = (
  currentIndex: number,
  issuePages: string[],
): boolean => (currentIndex === issuePages.length - 1);

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  multiple?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function PageCommonIssues({
  nextPage,
  nextOrSubmitButton,
  multiple = true,
  actionPage,
}: Props) {
  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  const [selectedOption, setSelectedOption] = useState<ReviewOfSystemOption[]>([]);
  const [selectedAllOption, setSelectedAllOption] = useState<ReviewOfSystemOption[]>([]);
  const [error, setError] = useState(false);
  const [state, dispatch] = usePersistMHReducer();
  const [options, setOptions] = useState<ReviewOfSystemOption[]>([]);

  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [issuePages, setIssuePages] = useState<string[]>([]);

  useEffect(() => {
    if (state) {
      const recentIssues = state.find(
        (obj: StateTypeLocalStorage) => obj.name === actionTypeOfMedicalHistory.ANY_RECENT_ISSUES,
      );
      const orderMap = new Map(
        reviewOfSystemMappingData.map((item, index) => [item.name, index]),
      );
      const sorted = [...recentIssues.value].sort((a: string, b: string) => {
        const idxA = orderMap.get(a) ?? Infinity;
        const idxB = orderMap.get(b) ?? Infinity;
        return idxA - idxB;
      });
      setIssuePages(sorted);
    }
  }, [state]);

  useEffect(() => {
    if (issuePages.length > 0) {
      const options = reviewOfSystemMappingData.find(
        (obj: CommonIssueItem) => obj.name === issuePages[currentPageIndex],
      );

      const mappingReviewOfSystem = patient?.reviewOfSystem?.reviewOfSystemElements?.filter(
        (item) => options?.anchor === item.discriminator,
      ).map((value) => (
        {
          name: value.title,
          discriminator: value.discriminator,
        }
      )) as ReviewOfSystemOption[];
      if (options) {
        const normalizedOptions = options.options.map((item) => (
          {
            name: item,
            discriminator: options.anchor,
            readOnly: false,
          }
        ));
        const {
          selectedOption,
          updatedOptions,
        } = mapOptionWithSelectedData(normalizedOptions ?? [], mappingReviewOfSystem);
        setOptions(updatedOptions);
        if (selectedOption?.length) {
          setSelectedOption(selectedOption);
        }
      }
    }
  }, [currentPageIndex, issuePages]);

  const clickNextOrSubmit = async (nextPage: number) => {
    if (selectedOption.length === 0) {
      setError(true);
      return;
    }

    setSelectedAllOption((prev) => [...prev, ...selectedOption]);

    if (!getSubmitState(currentPageIndex, issuePages)) {
      setCurrentPageIndex((old) => old + 1);
      setSelectedOption([]);
      return;
    }

    await dispatch({
      type: actionPage,
      name: 'Any recent issues common page',
      value: [...selectedAllOption, ...selectedOption],
    });
    nextOrSubmitButton(nextPage, getSubmitState(currentPageIndex, issuePages));
  };

  const onSelectOptions = useCallback((item: ReviewOfSystemOption) => {
    if (multiple) {
      setSelectedOption((old) => {
        if (old.find((value) => value.name === item.name)) {
          return old.filter((data) => data.name !== item.name);
        } return [...old, item];
      });
    } else {
      setSelectedOption([item]);
    }
  }, []);

  return (
    <>
      <MetaTag title={issuePages[currentPageIndex]} />
      <Box pb={4} mb={4}>
        <Box>
          <h2 className={`marginTopZero ${styles.titleText}`}>
            {issuePages[currentPageIndex]}
          </h2>
          <p className={styles.titleTextSubText}>
            Have you experienced any of the following in the last six months?
          </p>
          {error && (
            <p className="error-message">
              Please select at least one option.
            </p>
          )}
          <Stack>
            {options.map((option) => {
              const selected = selectedOption.find(
                (item) => item.name === option.name,
              );
              return (
                <p
                  key={option.name}
                  className={
                    clsx(
                      'option',
                      { 'selected-option': selected },
                      { 'cursor-pointer': !selected?.readOnly },
                      { '!cursor-not-allowed !text-gray-500 !bg-gray-200 font-medium !border-gray-200': selected?.readOnly },
                    )
                  }
                  onKeyUp={() => {}}
                  onClick={() => {
                    if (!selected?.readOnly) {
                      onSelectOptions(option);
                    }
                  }}
                >
                  {option.name}
                </p>
              );
            })}
          </Stack>
        </Box>
        <div className="next-button-fixed">
          <Button onClick={() => clickNextOrSubmit(nextPage)}>
            {getSubmitState(currentPageIndex, issuePages) ? 'Submit' : 'Next'}
          </Button>
        </div>
      </Box>
    </>
  );
}

export default PageCommonIssues;
